/** Runtime config, injected by /config.js (see config.template.js). */
export interface AdminConfig {
  engineBaseUrl: string;
  googleClientId: string;
}

declare global {
  interface Window {
    BEAN_ADMIN_CONFIG?: Partial<AdminConfig>;
    google?: GoogleIdentityServices;
  }
}

/** Minimal typings for the bits of Google Identity Services we use. */
export interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(opts: {
        client_id: string;
        callback: (res: { credential: string }) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        hd?: string;
      }): void;
      renderButton(
        parent: HTMLElement,
        opts: Record<string, string | number>,
      ): void;
      prompt(): void;
      disableAutoSelect(): void;
    };
  };
}

/**
 * envsubst leaves the literal `${VAR}` in place when a variable is unset, and the
 * committed dev stub ships empty strings. Treat both as "not set" — otherwise the
 * app silently calls a URL named "${ENGINE_BASE_URL}".
 */
function unset(v: string | undefined): boolean {
  return !v || v.startsWith("${");
}

/**
 * Runtime config wins; Vite env is the fallback.
 *
 * In a deployed container entrypoint.sh writes real values into /config.js, so
 * `window.BEAN_ADMIN_CONFIG` is authoritative and one image runs in every
 * environment. For `npm run dev` that file is the empty committed stub, so the
 * values come from `.env.local` instead — which is how you point local dev at a
 * deployed engine without editing tracked files.
 *
 * Precedence deliberately favours runtime: a stray VITE_* set at build time must
 * never silently override what the container was configured with.
 */
function read(): AdminConfig {
  const runtime = window.BEAN_ADMIN_CONFIG ?? {};
  const engineBaseUrl = unset(runtime.engineBaseUrl)
    ? (import.meta.env.VITE_ENGINE_BASE_URL ?? "")
    : runtime.engineBaseUrl!;
  const googleClientId = unset(runtime.googleClientId)
    ? (import.meta.env.VITE_ADMIN_GOOGLE_CLIENT_ID ?? "")
    : runtime.googleClientId!;
  return {
    // Trailing slashes would produce "//admin/me" against some proxies.
    engineBaseUrl: engineBaseUrl.replace(/\/+$/, ""),
    googleClientId,
  };
}

export const config = read();

export function configError(): string {
  if (unset(config.engineBaseUrl))
    return "Engine URL is not configured (VITE_ENGINE_BASE_URL or ENGINE_BASE_URL).";
  if (unset(config.googleClientId))
    return "Google client id is not configured (VITE_ADMIN_GOOGLE_CLIENT_ID or ADMIN_GOOGLE_CLIENT_ID).";
  return "";
}

/** Shown in the UI so it is always obvious which engine you are pointed at. */
export const engineLabel = config.engineBaseUrl.replace(/^https?:\/\//, "");
